import { create } from "zustand";
import { fetchIoTSiteConfig, type IoTSiteConfig } from "@/services/config";
import { getApiBaseUrl } from "@/utils/apiUtils";
import {
  isValidHexColor,
  generateColorPalette,
  getDefaultPalette,
  applyColorPaletteToDom,
} from "@/utils/colorUtils";
import { useThemeStore } from "@/hooks/useTheme";

const SITE_CONFIG_CACHE_KEY = "site_config_cache";

interface SiteConfigState {
  siteConfig: IoTSiteConfig | null;
  isLoading: boolean;
  isReady: boolean;
  error: string | null;

  loadSiteConfig: () => Promise<void>;
  init: () => Promise<void>;
  applyStandardColorToDom: (color?: string) => boolean;
}

function loadFromCache(): IoTSiteConfig | null {
  try {
    const cached = localStorage.getItem(SITE_CONFIG_CACHE_KEY);
    if (cached) return JSON.parse(cached) as IoTSiteConfig;
  } catch (e) {
    console.warn("[siteConfigStore] Failed to parse cached site config", e);
    localStorage.removeItem(SITE_CONFIG_CACHE_KEY);
  }
  return null;
}

function saveToCache(config: IoTSiteConfig) {
  try {
    localStorage.setItem(SITE_CONFIG_CACHE_KEY, JSON.stringify(config));
  } catch (e) {
    console.warn("[siteConfigStore] Failed to save site config to cache", e);
  }
}

function hasConfigChanged(a: IoTSiteConfig, b: IoTSiteConfig): boolean {
  return JSON.stringify(a) !== JSON.stringify(b);
}

export const useSiteConfigStore = create<SiteConfigState>((set, get) => ({
  siteConfig: null,
  isLoading: false,
  isReady: false,
  error: null,

  applyStandardColorToDom: (color) => {
    try {
      const isDark = useThemeStore.getState().theme === "dark";
      const config = get().siteConfig;
      const standardColor = isValidHexColor(config?.standard_color || "")
        ? config!.standard_color!
        : "#ff9500";
      const darkStandardColor = isValidHexColor(config?.dark_standard_color || "")
        ? config!.dark_standard_color!
        : "#ff9500";

      const colorToApply = color || (isDark ? darkStandardColor : standardColor);

      if (!isValidHexColor(colorToApply)) {
        console.warn(
          `[siteConfigStore] Invalid standard color: ${colorToApply}, using default palette`,
        );
        return applyColorPaletteToDom(getDefaultPalette());
      }

      const palette = generateColorPalette(colorToApply);
      return applyColorPaletteToDom(palette);
    } catch (e) {
      console.error("[siteConfigStore] Error applying standard color:", e);
      return applyColorPaletteToDom(getDefaultPalette());
    }
  },

  loadSiteConfig: async () => {
    if (get().isLoading) return;

    try {
      set({ isLoading: true, error: null });

      // 1. Hydrate from localStorage cache immediately for instant rendering.
      const cached = loadFromCache();
      if (cached && !get().siteConfig) {
        set({ siteConfig: cached });
      }

      // 2. Apply current color (from cache) to avoid a flash of the default palette.
      if (get().siteConfig) {
        get().applyStandardColorToDom();
      }

      // 3. Background fetch fresh config.
      const fresh = await fetchIoTSiteConfig();

      // 4. Only update reactive state if data actually changed.
      const current = get().siteConfig;
      if (!current || hasConfigChanged(current, fresh)) {
        const previousColor = current?.standard_color;
        set({ siteConfig: fresh });
        if (fresh.standard_color !== previousColor) {
          get().applyStandardColorToDom();
        }
      }

      saveToCache(fresh);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Failed to load site configuration";
      set({ error: msg });
      console.error("❌ Error loading site configuration:", err);
    } finally {
      set({ isLoading: false, isReady: true });
    }
  },

  init: async () => {
    if (get().isReady) return;

    if (!get().siteConfig && !get().isLoading) {
      await get().loadSiteConfig();
    } else if (get().isLoading) {
      // A parallel call is in flight — wait for it before returning.
      while (get().isLoading) {
        await new Promise((r) => setTimeout(r, 16));
      }
    }

    // Subscribe to theme changes once, re-applying the color palette.
    let lastTheme = useThemeStore.getState().theme;
    useThemeStore.subscribe((state) => {
      if (state.theme !== lastTheme) {
        lastTheme = state.theme;
        get().applyStandardColorToDom();
      }
    });
  },
}));

// Selectors / derived helpers — consumers can use these in React components.
export function selectProductName(s: SiteConfigState): string {
  return s.siteConfig?.product_name || "MaxMonitor";
}

export function selectLogoUrl(s: SiteConfigState): string | null {
  const logo = s.siteConfig?.logo;
  if (!logo) return null;
  if (logo.startsWith("http")) return logo;
  return `${getApiBaseUrl()}${logo}`;
}

export function selectDarkLogoUrl(s: SiteConfigState): string | null {
  const dark = s.siteConfig?.dark_logo;
  if (!dark) return null;
  if (dark.startsWith("http")) return dark;
  return `${getApiBaseUrl()}${dark}`;
}

export function selectApplicationIconUrl(s: SiteConfigState): string | null {
  const icon = s.siteConfig?.application_icon;
  if (!icon) return null;
  if (icon.startsWith("http")) return icon;
  return `${getApiBaseUrl()}${icon}`;
}

export function selectStandardColor(s: SiteConfigState): string {
  const c = s.siteConfig?.standard_color;
  return isValidHexColor(c || "") ? c! : "#ff9500";
}

export function selectDarkStandardColor(s: SiteConfigState): string {
  const c = s.siteConfig?.dark_standard_color;
  return isValidHexColor(c || "") ? c! : "#ff9500";
}

export function selectWhatsappLink(s: SiteConfigState): string | null {
  const phone = s.siteConfig?.helpdesk_whatsapp;
  if (!phone) return null;
  const clean = phone.replace(/\D/g, "");
  return `https://wa.me/${clean}`;
}
